import { FormEvent, useCallback } from "react"
import { useSubscribedNewslettersContext } from "modules/newsletters/contexts"
import { RecaptchaValidation } from "modules/newsletters/components/SubscribeDialog/RecaptchaValidation"
import { usePageProps } from "common/hooks/data/usePageProps"
import { NewslettersPageProps } from "pages/newsletters/index.page"
import {
  ErrorResponseFormat,
  useToggleSubscription,
} from "modules/newsletters/hooks/useToggleSubscription"
import { useRegionCode } from "modules/i18n/hooks/useRegionCode"
import { useNotifications } from "common/components/NotificationBar/useNotifications"
import { useTranslations } from "modules/i18n/hooks/useTranslations"
import { useNewslettersV2Context } from "../../contexts"
import { SubscriptionForm } from "./SubscriptionForm"
import { SuccessMessage } from "./SuccessMessage"
import { StickySubscriptionBar } from "./StickySubscriptionBar"

/**
 * Email input section with subscription form.
 * Shows different states for input, and success.
 */
export const EmailInputSection = () => {
  const { locale } = usePageProps<NewslettersPageProps>()
  const { email, isSubscribed, setIsSubscribed, recaptchaRef } = useNewslettersV2Context()
  const { ids: newsletterListIds } = useSubscribedNewslettersContext()
  const { toggleSubscription, isLoading } = useToggleSubscription()
  const regionCode = useRegionCode()
  const { set: setNotifications } = useNotifications()
  const t = useTranslations("newsletters")

  const selectedCount = newsletterListIds.size

  const handleSubscribe = useCallback(
    async (e?: FormEvent) => {
      e?.preventDefault()

      if (!email || selectedCount === 0) {
        return
      }

      const recaptchaToken = (await recaptchaRef.current?.executeAsync()) ?? undefined

      try {
        await toggleSubscription({
          regionCode,
          email,
          action: "subscribe",
          newsletterListIds,
          recaptchaToken,
        })

        setIsSubscribed(true)
      } catch (error) {
        const errorMessage =
          (error as ErrorResponseFormat)?.errors?.[0] ?? t("v2.email_section.error")
        setNotifications([{ type: "error", title: errorMessage }])
      }
    },
    [
      email,
      selectedCount,
      recaptchaRef,
      toggleSubscription,
      newsletterListIds,
      regionCode,
      setIsSubscribed,
      setNotifications,
      t,
    ],
  )

  return (
    <>
      <RecaptchaValidation recaptchaRef={recaptchaRef} locale={locale} />

      {/* Hero section with heading and description */}
      <div className="mx-auto max-w-xl pt-4 pb-6 text-center">
        {!isSubscribed && (
          <p className="font-serif text-lg leading-relaxed">{t("v2.email_section.description")}</p>
        )}
      </div>

      <StickySubscriptionBar>
        {isSubscribed ? (
          <SuccessMessage />
        ) : (
          <SubscriptionForm
            onSubmit={handleSubscribe}
            isLoading={isLoading}
            selectedCount={selectedCount}
          />
        )}
      </StickySubscriptionBar>
    </>
  )
}
